import type { Operation } from 'oas/operation';

import reducer from 'oas/reducer';
import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { ProjectContext } from '@core/context';
import useClassy from '@core/hooks/useClassy';
import useLocalStorage from '@core/hooks/useLocalStorage';
import { useProjectStore, useSuperHubStore } from '@core/store';

import Button from '@ui/Button';
import Dropdown from '@ui/Dropdown';
import Icon from '@ui/Icon';
import Menu, { MenuDivider, MenuItem } from '@ui/Menu';
import Notification, { notify } from '@ui/Notification';
import Sparkle from '@ui/Sparkle';

import styles from './index.module.scss';
import { useMarkdownUrl, useMarkdownUrlPrompt } from './useMarkdownUrl';

// Define the AI option types
type AIOption = 'askai' | 'chatgpt' | 'claude' | 'clipboard' | 'copilot' | 'markdown';

interface AIDropdownProps {
  className?: string;
  doc?: { deprecated?: boolean; excerpt?: string; slug: string; title: string; type?: string };
  // These can now be optional as the component can fetch them itself
  enableAskAI?: boolean;
  enableChatGPT?: boolean;
  enableClaude?: boolean;
  enableClipboard?: boolean;
  enableCopilot?: boolean;
  enableViewAsMarkdown?: boolean;
  isPreview?: boolean;
  // New props for OAS enhancement
  operation?: Operation;
  // Add new props to control visibility
  showAIDropdown?: boolean;
}

/**
 * AIDropdown component for sharing the current page URL with AI tools like ChatGPT and Claude
 * This dropdown provides options to open the current page in various AI assistants
 * or copy the URL to the clipboard.
 *
 * The component can now determine its own visibility and fetch AI options from the project store.
 */
const AIDropdown: React.FC<React.PropsWithChildren<AIDropdownProps>> = ({
  className,
  // We'll use these props as fallbacks if provided
  enableAskAI: enableAskAIProp,
  enableChatGPT: enableChatGPTProp,
  enableClaude: enableClaudeProp,
  enableClipboard: enableClipboardProp,
  enableCopilot: enableCopilotProp,
  enableViewAsMarkdown: enableViewAsMarkdownProp,
  isPreview = false,
  // New props for visibility control
  showAIDropdown = false,
  // New props for OAS enhancement
  operation,
  doc,
}) => {
  const { t } = useTranslation();
  // Get the AI options from the project store
  const [
    storeEnableAskAI,
    storeEnableChatGPT,
    storeEnableClaude,
    storeEnableClipboard,
    storeEnableCopilot,
    storeEnableViewAsMarkdown,
    aiDropdownEnabledFromStore,
  ] = useProjectStore(s => [
    s.data.appearance.ai?.options?.ask_ai === 'enabled',
    s.data.appearance.ai?.options?.chatgpt === 'enabled',
    s.data.appearance.ai?.options?.claude === 'enabled',
    s.data.appearance.ai?.options?.clipboard === 'enabled',
    s.data.appearance.ai?.options?.copilot === 'enabled',
    s.data.appearance.ai?.options?.view_as_markdown === 'enabled',
    s.data.appearance.ai?.dropdown === 'enabled',
  ]);
  const [isSuperHub, toggleOwlbot] = useSuperHubStore(s => [s.isSuperHub, s.toggleOwlbot]);

  const markdownUrl = useMarkdownUrl();
  const getMarkdownUrlPrompt = useMarkdownUrlPrompt();

  // Use props if provided, otherwise use store values
  const enableAskAI = enableAskAIProp !== undefined ? enableAskAIProp : storeEnableAskAI;
  const enableChatGPT = enableChatGPTProp !== undefined ? enableChatGPTProp : storeEnableChatGPT;
  const enableClaude = enableClaudeProp !== undefined ? enableClaudeProp : storeEnableClaude;
  const enableClipboard = enableClipboardProp !== undefined ? enableClipboardProp : storeEnableClipboard;
  const enableCopilot = enableCopilotProp !== undefined ? enableCopilotProp : storeEnableCopilot;
  const enableViewAsMarkdown =
    enableViewAsMarkdownProp !== undefined ? enableViewAsMarkdownProp : storeEnableViewAsMarkdown;

  const { project } = useContext(ProjectContext);

  // Check if Ask AI can actually be used (both enabled in settings AND Owlbot is available)
  // Note: We don't have project context here, so we'll assume if storeEnableAskAI is true, project allows it
  const canUseAskAI = enableAskAI && project.owlbot?.enabled;

  /**
   * Fetches clean and enhanced markdown content from the same route as the
   * `View as Markdown` link. This ensures the MD content is consistent with
   * each other and is sanitized.
   */
  const getEnhancedMarkdownContent = useCallback(async () => {
    let result = doc?.title || '';

    const markdownContent = await fetch(markdownUrl)
      .then(res => res.text())
      .catch(() => 'Something went wrong getting markdown content.');

    if (markdownContent) {
      result = `${result}\n\n${markdownContent}`;
    }

    if (operation) {
      try {
        const reducedOas = reducer(operation.api, { paths: { [operation.path]: [operation.method] } });
        const oasMarkdown = `# OpenAPI definition\n\`\`\`json\n${JSON.stringify(reducedOas, null, 2)}\n\`\`\``;
        result = `${result}\n\n${oasMarkdown}`;
      } catch (error) {
        // Silently catch the error
      }
    }

    return result;
  }, [doc?.title, markdownUrl, operation]);

  // Determine if we should show the AI dropdown
  // For SuperHub mode, only rely on the prop passed down
  // For non-SuperHub mode, check both the prop and the store setting
  const shouldShowAIDropdown = isSuperHub
    ? showAIDropdown // SuperHub: only use the prop
    : showAIDropdown || aiDropdownEnabledFromStore; // Non-SuperHub: use prop or store

  const [, setIsMounted] = useState(false);
  // State to track screen width
  const [isSmallScreen, setIsSmallScreen] = useState(false);

  // Use effect to mark component as mounted and track screen size
  useEffect(() => {
    setIsMounted(true);

    // Force a re-render of dropdown styling after a short delay
    const timer = setTimeout(() => {
      setIsMounted(state => !state);
      setTimeout(() => setIsMounted(true), 50);
    }, 500);

    // Function to update screen size state
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 1250);
    };

    // Set initial screen size state
    handleResize();

    // Add event listener for resize
    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const bem = useClassy(styles, 'AIDropdown');

  // Local storage hook to remember the last used AI option
  const localStorage = useLocalStorage({ json: true });
  const [lastUsedOption, setLastUsedOption] = useState<AIOption | null>(
    localStorage.getItem('last-used-ai-option') || null,
  );

  const showSuccessToast = (message: string) => {
    notify(<Notification kind="success" title={message} />, { duration: 3000, position: 'bottom-right' });
  };

  const showErrorToast = (message: string) => {
    notify(<Notification kind="error" title={message} />, { duration: 3000, position: 'bottom-right' });
  };

  const copyToClipboard = async (text: string) => {
    // First try the modern navigator.clipboard API
    if (navigator.clipboard && navigator.clipboard.writeText) {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch (error) {
        // Fall through to fallback methods
      }
    }

    // Fallback using document.execCommand (deprecated but still works in many browsers)
    try {
      const textArea = document.createElement('textarea');
      textArea.value = text;

      // Make the textarea out of viewport
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);

      textArea.focus();
      textArea.select();

      const success = document.execCommand('copy');
      document.body.removeChild(textArea);
      return success;
    } catch (error) {
      return false;
    }
  };

  const handleOpenLink = (itemName: AIOption, itemLabel: string) => {
    if (isPreview) return;

    try {
      // Save as last used option
      localStorage.setItem('last-used-ai-option', itemName);
      setLastUsedOption(itemName);
    } catch (error) {
      showErrorToast(t('ai.openFailed', { option: itemLabel }));
    }
  };

  const handleCopyToClipboard = async () => {
    if (isPreview) return;

    const enhancedMarkdownContent = await getEnhancedMarkdownContent();

    // Only proceed if we have enhanced markdown content, otherwise show an error
    if (!enhancedMarkdownContent) {
      showErrorToast(t('ai.noMdToCopy'));
      return;
    }

    copyToClipboard(enhancedMarkdownContent)
      .then(success => {
        if (success) {
          // Save as last used option
          localStorage.setItem('last-used-ai-option', 'clipboard');
          setLastUsedOption('clipboard');

          showSuccessToast(t('copyToClipboard.copiedFull'));
        } else {
          showErrorToast(t('copyToClipboard.failed'));
        }
      })
      .catch(() => {
        showErrorToast(t('copyToClipboard.failed'));
      });
  };

  const handleViewAsMarkdown = () => {
    if (isPreview) return;

    try {
      // Open the markdown version in a new tab, without showing any toast
      window.open(markdownUrl, '_blank');

      // Save as last used option
      localStorage.setItem('last-used-ai-option', 'markdown');
      setLastUsedOption('markdown');

      // No toast notification
    } catch (error) {
      // Only show an error toast if something goes wrong
      showErrorToast(t('ai.mdOpenFailed'));
    }
  };

  const handleAskAI = () => {
    if (isPreview) return;

    try {
      // Toggle the Owlbot chat panel in the SuperHub store
      toggleOwlbot();

      // Save as last used option
      setLastUsedOption('askai');
    } catch (error) {
      showErrorToast(t('ai.aiOpenFailed'));
    }
  };

  // Structure for AI options to facilitate rendering and reordering
  const aiOptions = [
    {
      id: 'chatgpt',
      enabled: enableChatGPT,
      handler: () => handleOpenLink('chatgpt', 'ChatGPT'),
      icon: 'openai',
      label: 'ChatGPT',
      href: `https://chat.openai.com/?q=${getMarkdownUrlPrompt('ChatGPT')}`,
    },
    {
      id: 'claude',
      enabled: enableClaude,
      handler: () => handleOpenLink('claude', 'Claude'),
      icon: 'claude',
      label: 'Claude',
      href: `https://claude.ai/new?q=${getMarkdownUrlPrompt('Claude')}`,
    },
    {
      id: 'copilot',
      enabled: enableCopilot,
      handler: () => handleOpenLink('copilot', 'Copilot'),
      icon: 'copilot',
      label: 'Copilot',
      href: `https://copilot.microsoft.com/?q=${getMarkdownUrlPrompt('Copilot')}`,
    },
    // Only include Ask AI if it's both enabled and available
    ...(canUseAskAI
      ? [{ id: 'askai', enabled: enableAskAI, handler: handleAskAI, icon: 'sparkles', label: t('ai.askAi') }]
      : []),
  ];

  const utilityOptions = [
    {
      id: 'clipboard',
      enabled: enableClipboard,
      handler: handleCopyToClipboard,
      icon: 'clipboard',
      label: t('ai.mdCopy'),
    },
    {
      id: 'markdown',
      enabled: enableViewAsMarkdown,
      handler: handleViewAsMarkdown,
      icon: 'markdown',
      label: t('ai.mdView'),
    },
  ];

  // Sort AI options to put the last used one first
  const sortedAIOptions = [...aiOptions].sort((a, b) => {
    if (a.id === lastUsedOption) return -1;
    if (b.id === lastUsedOption) return 1;
    return 0;
  });

  // Sort utility options if one was last used
  const sortedUtilityOptions = [...utilityOptions].sort((a, b) => {
    if (a.id === lastUsedOption) return -1;
    if (b.id === lastUsedOption) return 1;
    return 0;
  });

  // Check if any option is enabled and if the dropdown should be shown
  const hasEnabledOptions =
    enableChatGPT ||
    enableClaude ||
    enableCopilot ||
    enableClipboard ||
    enableViewAsMarkdown ||
    (enableAskAI && canUseAskAI);
  const hasEnabledNonAskAIOptions =
    enableChatGPT || enableClaude || enableCopilot || enableClipboard || enableViewAsMarkdown;
  const hasEnabledAIOptionsExcludingAskAI = enableChatGPT || enableClaude || enableCopilot;

  // Don't render the dropdown if no options are enabled or if it shouldn't be shown
  if (!hasEnabledOptions || !shouldShowAIDropdown) {
    return null;
  }

  // Enhanced style to ensure visibility - force inlined styles
  const dropdownStyle = {
    display: 'flex',
    justifyContent: 'flex-start',
    width: '100%',
    marginBottom: '1rem',
    zIndex: 50,
  };

  // Check if there are any enabled AI and utility options
  const hasEnabledAIOptions = sortedAIOptions.some(opt => opt.enabled);
  const hasEnabledUtilityOptions = sortedUtilityOptions.some(opt => opt.enabled);

  // Find the last used option from all options
  const lastUsedItem = [...aiOptions, ...utilityOptions].find(opt => opt.id === lastUsedOption && opt.enabled);

  // When Ask AI is enabled, show split button behavior
  if (enableAskAI && canUseAskAI) {
    return (
      <div className={`${bem('&')} ${className || ''} rm-AIDropdown`} style={dropdownStyle}>
        {/* Split button design when Ask AI is enabled */}
        {hasEnabledNonAskAIOptions ? (
          <Dropdown align="bottom" appendTo={() => document.body} justify="start" sticky>
            <div style={{ display: 'flex' }}>
              {/* Main Ask AI button */}
              <Button
                kind="secondary"
                onClick={e => {
                  e.stopPropagation(); // Prevent dropdown from opening
                  handleAskAI();
                }}
                outline
                size="sm"
                style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0, borderRight: 'none' }}
              >
                <Sparkle />
                {!isSmallScreen && <span>{t('ai.askAi')}</span>}
              </Button>

              {/* Dropdown arrow button */}
              <Button
                kind="secondary"
                outline
                size="sm"
                style={{
                  borderTopLeftRadius: 0,
                  borderBottomLeftRadius: 0,
                  paddingLeft: '8px',
                  paddingRight: '8px',
                  minWidth: 'auto',
                }}
              >
                <Icon aria-hidden="true" name="chevron-down" />
              </Button>
            </div>
            <Menu>
              {/* Show last used option at the very top if it exists and it's not Ask AI */}
              {!!lastUsedItem && lastUsedItem.id !== 'askai' && (
                <>
                  <MenuItem description="Last Used" focusable={false} />
                  <MenuItem
                    key={`last-used-${lastUsedItem.id}`}
                    onClick={lastUsedItem.handler}
                    {...('href' in lastUsedItem && typeof lastUsedItem.href === 'string'
                      ? { href: lastUsedItem.href, target: '_blank', TagName: 'a' }
                      : {})}
                  >
                    <Icon aria-hidden="true" name={lastUsedItem.icon} /> {lastUsedItem.label}
                  </MenuItem>
                  <MenuDivider />
                </>
              )}

              {/* Render AI options (excluding Ask AI and the last used one) */}
              {aiOptions
                .filter(opt => opt.enabled && opt.id !== 'askai' && opt.id !== lastUsedOption)
                .map(opt => (
                  <MenuItem key={opt.id} href={opt.href} onClick={opt.handler} TagName="a" target="_blank">
                    <Icon aria-hidden="true" name={opt.icon} /> {opt.label}
                  </MenuItem>
                ))}

              {/* Add divider between AI and utility options if both exist */}
              {!!hasEnabledAIOptionsExcludingAskAI &&
                !!hasEnabledUtilityOptions &&
                utilityOptions.some(opt => opt.enabled && opt.id !== lastUsedOption) && <MenuDivider />}

              {/* Render utility options (excluding the last used one) */}
              {utilityOptions
                .filter(opt => opt.enabled && opt.id !== lastUsedOption)
                .map(opt => (
                  <MenuItem key={opt.id} onClick={opt.handler}>
                    <Icon aria-hidden="true" name={opt.icon} /> {opt.label}
                  </MenuItem>
                ))}
            </Menu>
          </Dropdown>
        ) : (
          /* If no other options are enabled, just show the Ask AI button without dropdown */
          <Button kind="secondary" onClick={handleAskAI} outline size="sm">
            <Sparkle />
            {!isSmallScreen && <span>{t('ai.askAi')}</span>}
          </Button>
        )}
      </div>
    );
  }

  // Original dropdown behavior when Ask AI is disabled
  return (
    <div className={`${bem('&')} ${className || ''} rm-AIDropdown`} style={dropdownStyle}>
      <Dropdown align="bottom" appendTo={() => document.body} justify="start" sticky>
        <Button kind="secondary" outline size="sm">
          <Sparkle />
          {!isSmallScreen && <span>{t('ai.askAi')}</span>}
          <Icon aria-hidden="true" name="chevron-down" />
        </Button>
        <Menu>
          {/* Show last used option at the very top if it exists */}
          {!!lastUsedItem && (
            <>
              <MenuItem description="Last Used" focusable={false} />
              <MenuItem
                key={`last-used-${lastUsedItem.id}`}
                {...('href' in lastUsedItem && typeof lastUsedItem.href === 'string'
                  ? { href: lastUsedItem.href, target: '_blank', TagName: 'a' }
                  : {})}
                onClick={lastUsedItem.handler}
              >
                <Icon name={lastUsedItem.icon} /> {lastUsedItem.label}
              </MenuItem>
              <MenuDivider />
            </>
          )}

          {/* Render AI options (excluding the last used one) */}
          {aiOptions
            .filter(opt => opt.enabled && opt.id !== lastUsedOption)
            .map(opt => (
              <MenuItem key={opt.id} href={opt.href} onClick={opt.handler} TagName="a" target="_blank">
                <Icon aria-hidden="true" name={opt.icon} /> {opt.label}
              </MenuItem>
            ))}

          {/* Add divider between AI and utility options if both exist */}
          {!!hasEnabledAIOptions &&
            !!hasEnabledUtilityOptions &&
            utilityOptions.some(opt => opt.enabled && opt.id !== lastUsedOption) && <MenuDivider />}

          {/* Render utility options (excluding the last used one) */}
          {utilityOptions
            .filter(opt => opt.enabled && opt.id !== lastUsedOption)
            .map(opt => (
              <MenuItem key={opt.id} onClick={opt.handler}>
                <Icon aria-hidden="true" name={opt.icon} /> {opt.label}
              </MenuItem>
            ))}
        </Menu>
      </Dropdown>
    </div>
  );
};

export default AIDropdown;
